import { distinctUntilChanged, filter, map, Observable, Subject } from 'rxjs';
import { isEqual } from 'lodash';
import { fabric } from 'fabric';
import { isBoardItem } from '../../../common/y-session';
import { ensureAllObjectAreLive, YCanvas, YStaticCanvas } from '../../wb-canvas/fabric-utils';
import { FabricObject } from './board-items-manager';
import { BoardItem } from './board.item';
import { BoardItemConfig } from './board-item-configurations';

/**
 * Manages the state synchronization between local and remote board items.
 * This class handles:
 * - State updates between local and remote instances
 * - Canvas object synchronization
 * - State change notifications through RxJS observables
 * - Fabric.js object state management
 *
 * The manager ensures that board items maintain consistent state across:
 * - Local canvas modifications
 * - Remote updates
 * - Object selection and modification states
 */
export class BoardItemsStateManager {
  private canvas!: YCanvas | YStaticCanvas;
  private _state$ = new Subject<BoardItem>();

  constructor(canvas: YCanvas | YStaticCanvas) {
    this.canvas = canvas;
  }

  // TODO <Hatahet> make this configurations instead (maybe we need to wait until we have everything (including fabric object) use board items)
  updateLocalState(boardItem: BoardItem) {
    this._state$.next(boardItem);
  }

  updateRemoteState(object: fabric.Object) {
    object.canvas?.fire('object:modified', { target: object });
  }

  onStateUpdated$<T extends BoardItemConfig>(itemId: string): Observable<T> {
    return this._state$.pipe(
      filter((component) => component.configurations.uid === itemId),
      map((boardItem) => boardItem.configurations as T),
      distinctUntilChanged((a, b) => isEqual(a, b)),
    );
  }

  public updateObjectsInLocalCanvas(objectsModified: FabricObject[]) {
    if (objectsModified.length === 0) {
      return;
    }

    ensureAllObjectAreLive(objectsModified, (fabricObjects) => {
      // For each object coming from the server, we need its corresponding instance and position in the local canvas so we can edit it or replace it.
      const objectsUidToLocalObject = new Map<
        string,
        { index: number; localObject: FabricObject }
      >();

      const localObjects = this.canvas.getObjects() as FabricObject[];
      localObjects.forEach((localObject, index) => {
        const objectUid = localObject.uid;
        if (objectUid) {
          objectsUidToLocalObject.set(objectUid, {
            index,
            localObject: localObjects[index],
          });
        }
      });

      for (const object of fabricObjects) {
        if (!object.uid) {
          continue;
        }
        const remoteObjectInLocalCanvas = objectsUidToLocalObject.get(object.uid);
        if (remoteObjectInLocalCanvas !== undefined) {
          const { index, localObject } = remoteObjectInLocalCanvas;
          this.updateFabricObjectState(object, localObject, index, this.canvas);
        }
      }
    });
  }

  // Two types of items are updated differently:
  //   1 - Fabric object => we replace them
  //   2 - Board Item => we update the state using an observable
  updateFabricObjectState(
    remoteObject: fabric.Object,
    currentObject: fabric.Object,
    objectIndex: number,
    canvas: YCanvas | YStaticCanvas,
  ) {
    if (isBoardItem(remoteObject)) {
      this.updateLocalState(remoteObject);
    } else {
      // This should have the same behavior as calling canvas.insertAt(remoteObject, objectIndex, true),
      // the difference is that we don't want the onObjectAdded callback to fire object:added events to not interrupt how we process updates
      // emitting object:added here might in some scenarios re-add an already removed object which is something we want to avoid
      canvas._objects[objectIndex] = remoteObject;
      canvas.stateful && remoteObject.setupState();
      remoteObject._set('canvas', canvas);
      remoteObject.setCoords();
    }

    this.setSelectabilityFromLocalObject(remoteObject, currentObject);
  }

  private setSelectabilityFromLocalObject(remoteObject: any, localObject: any) {
    // in the case of having the space/board locked for the current user while not locked for the remote user who updated the object,
    // We want to keep some values that are dependent on the current state of the user from being affected from the remote one
    remoteObject.selectable = localObject.selectable;
    remoteObject.hoverCursor = localObject.hoverCursor;
  }
}
